import React, { useCallback, useEffect, useState } from 'react';
import { getDuration, Inscription, useReliableInterval } from '@common-fe/common-fe';

import { MINUTE, SECOND } from '@/common/constants';
import { convertSecondsToMinutes } from '@/utils/modifiers/convertSecondsToMinutes';

const ONE_MINUTE_IN_SECONDS = MINUTE / SECOND;

interface Props {
  testId?: string;
  onResend: () => void;
  defaultRemainingTime?: number;
}

const ResendCodeButton: React.FC<Props> = ({ testId, onResend, defaultRemainingTime }) => {
  const [remainingTime, setRemainingTime] = useState(defaultRemainingTime || ONE_MINUTE_IN_SECONDS);
  const [isTimerActive, setIsTimerActive] = useState(remainingTime > 0);

  const handleResend = useCallback(() => {
    setIsTimerActive(true);
    setRemainingTime(ONE_MINUTE_IN_SECONDS);
    onResend();
  }, [onResend]);

  const updateRemainingTime = useCallback((value: number) => {
    const currentRemainingTime = value - 1;
    if (currentRemainingTime <= 0) {
      setIsTimerActive(false);
    }
    return currentRemainingTime;
  }, []);

  const { start, stop } =  useReliableInterval(() => {
    setRemainingTime(updateRemainingTime);
  },getDuration({seconds: 1}));

  useEffect(() => {
    if (isTimerActive) {
      start();
      return () => {
        stop();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerActive, updateRemainingTime]);

  return (
    <Inscription
      data-testId={`${testId}_resend-button`}
      color={isTimerActive ? 'textDisabled' : 'textAccent'}
      size="small"
      weight={500}
      {...isTimerActive ? {} : { onClick: handleResend, cursor: 'pointer' }}
    >
      {'Resend Code '} 
      {isTimerActive && `(${convertSecondsToMinutes(remainingTime)})`}
    </Inscription>
  );
};

export default ResendCodeButton;
